(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(root);
  } else {
    root.HLA_CART_SHARE = root.HLA_CART_SHARE || factory(root);
    const start = () => root.HLA_CART_SHARE.initAll();
    if (root.document && root.document.readyState === 'loading') {
      root.document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  const DEFAULT_MAX_MAILTO_URL_LENGTH = 1800;
  const DEFAULT_EMAIL_SUBJECT = 'Wholesale cart to review';
  const READY_HELPER = 'Shares items and quantities, not your pricing. Recipients must log in to see prices for their account.';
  const EMPTY_HELPER = 'Your cart is empty';
  const ERROR_HELPER = 'Unable to load your cart. Please try again.';

  // 2026-07-28 (Alexis, BC 10138280439): the old 2.4s "Link copied" toast vanished before
  // buyers could confirm anything. The link now opens in a popup that stays until dismissed
  // and shows the actual URL, so a failed clipboard write is still recoverable by hand.
  const MODAL_TITLE = 'Your cart link';
  const MODAL_TITLE_ID = 'hla-cart-share-modal-title';
  const MODAL_NOTE = 'Anyone you send this to gets your items and quantities, not your pricing. They will need to log in to see prices for their own account.';
  const COPY_SUCCESS_STATUS = 'Link copied to your clipboard.';
  const COPY_MANUAL_STATUS = 'Your browser blocked the copy. Select the link above and copy it.';

  function normalizeString(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function normalizeOrigin(origin) {
    return String(origin || '').trim().replace(/\/+$/, '');
  }

  function cartItems(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.map(item => {
      const variantId = normalizeString(item && (item.variant_id || item.id));
      const quantity = Math.floor(Number(item && item.quantity));
      if (!/^\d+$/.test(variantId) || !Number.isFinite(quantity) || quantity <= 0) return null;
      return { item, variantId, quantity };
    }).filter(Boolean);
  }

  function buildCartPermalink(cart, origin) {
    const base = normalizeOrigin(origin);
    const items = cartItems(cart);
    if (!base || items.length === 0) return '';
    const path = items.map(({ variantId, quantity }) => `${variantId}:${quantity}`).join(',');
    return `${base}/cart/${path}`;
  }

  function moneyFromCents(cents, currency = 'USD') {
    if (cents == null || cents === '') return 'Price unavailable';
    const value = Number(cents);
    if (!Number.isFinite(value)) return 'Price unavailable';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: normalizeString(currency) || 'USD',
      }).format(value / 100);
    } catch (_) {
      return `$${(value / 100).toFixed(2)}`;
    }
  }

  function itemPriceCents(item) {
    const candidates = [item && item.final_price, item && item.discounted_price, item && item.price];
    const value = candidates.find(candidate => Number.isFinite(Number(candidate)));
    return value == null ? null : Number(value);
  }

  function itemLinePriceCents(item, quantity) {
    const candidates = [item && item.final_line_price, item && item.line_price];
    const value = candidates.find(candidate => Number.isFinite(Number(candidate)));
    if (value != null) return Number(value);
    const packPrice = itemPriceCents(item);
    return packPrice == null ? null : packPrice * quantity;
  }

  function buildLineItemList(cart) {
    const currency = normalizeString(cart && cart.currency) || 'USD';
    return cartItems(cart).map(({ item, variantId, quantity }, index) => {
      const title = normalizeString(item.product_title || item.title) || 'Untitled item';
      const variantTitle = normalizeString(item.variant_title);
      const variantLabel = variantTitle && variantTitle.toLowerCase() !== 'default title'
        ? ` — ${variantTitle}`
        : '';
      const sku = normalizeString(item.sku);
      const identifier = sku ? `SKU ${sku}; variant ${variantId}` : `Variant ${variantId}`;
      const packPrice = moneyFromCents(itemPriceCents(item), currency);
      const linePrice = moneyFromCents(itemLinePriceCents(item, quantity), currency);
      return `${index + 1}. ${title}${variantLabel} — ${identifier} — Qty ${quantity} — Pack price ${packPrice} — Line total ${linePrice}`;
    }).join('\n');
  }

  function mailtoUrl(subject, body) {
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  function buildEmailCartUrl(cart, permalink, options = {}) {
    const items = cartItems(cart);
    if (items.length === 0) {
      return { url: '', subject: '', body: '', fallback: false };
    }

    const subject = normalizeString(options.subject) || DEFAULT_EMAIL_SUBJECT;
    const maxUrlLength = Number.isFinite(Number(options.maxUrlLength))
      ? Math.max(1, Math.floor(Number(options.maxUrlLength)))
      : DEFAULT_MAX_MAILTO_URL_LENGTH;
    const lineItems = buildLineItemList(cart);
    const pricingNote = 'This shares items and quantities only. Prices depend on the recipient\'s logged-in account.';
    const linkedBody = [
      'Here is my wholesale cart:',
      '',
      permalink,
      '',
      'Items:',
      lineItems,
      '',
      pricingNote,
    ].join('\n');
    const linkedUrl = mailtoUrl(subject, linkedBody);
    const fallback = !permalink || permalink.length > maxUrlLength || linkedUrl.length > maxUrlLength;
    if (!fallback) return { url: linkedUrl, subject, body: linkedBody, fallback: false };

    const fallbackBody = [
      'Here is my wholesale cart item list:',
      '',
      'The cart link was omitted because the email link would be too long. Please use the item list below.',
      '',
      'Items:',
      lineItems,
      '',
      pricingNote,
    ].join('\n');
    return {
      url: mailtoUrl(subject, fallbackBody),
      subject,
      body: fallbackBody,
      fallback: true,
    };
  }

  function buildSharePayload(cart, origin, options = {}) {
    const items = cartItems(cart);
    if (items.length === 0) {
      return {
        empty: true,
        permalink: '',
        email: buildEmailCartUrl(cart, '', options),
      };
    }
    const permalink = buildCartPermalink(cart, origin);
    return {
      empty: false,
      permalink,
      email: buildEmailCartUrl(cart, permalink, options),
    };
  }

  async function fetchCart() {
    if (typeof root.fetch !== 'function') throw new Error('Cart fetch is unavailable');
    const response = await root.fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
    return response.json();
  }

  function widgetElements(widget) {
    return {
      copy: widget.querySelector('[data-hla-cart-share-copy]'),
      email: widget.querySelector('[data-hla-cart-share-email]'),
      helper: widget.querySelector('[data-hla-cart-share-helper]'),
      status: widget.querySelector('[data-hla-cart-share-status]'),
    };
  }

  function setButtonsDisabled(widget, disabled) {
    const elements = widgetElements(widget);
    if (elements.copy) elements.copy.disabled = disabled;
    if (elements.email) elements.email.disabled = disabled;
  }

  function setWidgetState(widget, state, helperText, statusText = '') {
    const elements = widgetElements(widget);
    widget.dataset.hlaCartShareState = state;
    widget.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    if (elements.helper) elements.helper.textContent = helperText;
    if (elements.status) elements.status.textContent = statusText;
  }

  async function loadWidgetCart(widget) {
    setButtonsDisabled(widget, true);
    setWidgetState(widget, 'loading', READY_HELPER, 'Loading your cart.');
    try {
      const cart = await fetchCart();
      const empty = cartItems(cart).length === 0;
      widget.hlaCartShareCart = cart;
      if (empty) {
        setWidgetState(widget, 'empty', EMPTY_HELPER);
        return cart;
      }
      setButtonsDisabled(widget, false);
      setWidgetState(widget, 'ready', READY_HELPER);
      return cart;
    } catch (error) {
      widget.hlaCartShareCart = null;
      setWidgetState(widget, 'error', ERROR_HELPER);
      // Re-enable: the error copy says "Please try again", so the buttons have to
      // still be clickable. Leaving them disabled makes a transient /cart.js blip
      // permanently dead until the drawer re-renders.
      setButtonsDisabled(widget, false);
      throw error;
    }
  }

  function fallbackCopy(text) {
    const doc = root.document;
    if (!doc || typeof doc.execCommand !== 'function') return false;
    const field = doc.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    doc.body.appendChild(field);
    try {
      field.select();
      return doc.execCommand('copy');
    } finally {
      doc.body.removeChild(field);
    }
  }

  async function copyText(text) {
    try {
      if (root.navigator && root.navigator.clipboard && typeof root.navigator.clipboard.writeText === 'function') {
        await root.navigator.clipboard.writeText(text);
        return;
      }
    } catch (_) {
      // Some browsers expose Clipboard API without granting permission; use the legacy fallback.
    }
    if (!fallbackCopy(text)) throw new Error('Clipboard copy failed');
  }

  function el(doc, tag, className, text) {
    const node = doc.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  // One modal per document, no matter how many widgets are on the page (the drawer and the
  // cart page both render the snippet). It is appended to <body> on purpose: it must sit
  // outside the cart <form> so its buttons can never submit, and outside the drawer's focus
  // trap. Deliberately not <dialog>.showModal() and never alert() — a native modal dialog
  // blocks the page and, in the drawer, fights Dawn's own focus handling.
  function ensureModal(doc) {
    if (!doc || !doc.body) return null;
    if (doc.body.hlaCartShareModal) return doc.body.hlaCartShareModal;

    const rootNode = el(doc, 'div', 'hla-cart-share-modal');
    rootNode.setAttribute('data-hla-cart-share-modal', '');
    rootNode.hidden = true;

    const backdrop = el(doc, 'div', 'hla-cart-share-modal__backdrop');
    backdrop.setAttribute('data-hla-cart-share-modal-close', '');

    const panel = el(doc, 'div', 'hla-cart-share-modal__panel');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', MODAL_TITLE_ID);

    const title = el(doc, 'h2', 'hla-cart-share-modal__title', MODAL_TITLE);
    title.id = MODAL_TITLE_ID;

    const field = el(doc, 'input', 'hla-cart-share-modal__field');
    field.setAttribute('data-hla-cart-share-modal-field', '');
    field.setAttribute('type', 'text');
    field.setAttribute('readonly', '');
    field.setAttribute('aria-label', 'Cart link');
    field.readOnly = true;
    field.value = '';

    const status = el(doc, 'p', 'hla-cart-share-modal__status');
    status.setAttribute('data-hla-cart-share-modal-status', '');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    const note = el(doc, 'p', 'hla-cart-share-modal__note', MODAL_NOTE);

    const actions = el(doc, 'div', 'hla-cart-share-modal__actions');
    const copyButton = el(doc, 'button', 'button hla-cart-share-modal__copy', 'Copy link');
    copyButton.setAttribute('type', 'button');
    copyButton.setAttribute('data-hla-cart-share-modal-copy', '');
    const doneButton = el(doc, 'button', 'button button--secondary hla-cart-share-modal__done', 'Done');
    doneButton.setAttribute('type', 'button');
    doneButton.setAttribute('data-hla-cart-share-modal-close', '');
    actions.appendChild(copyButton);
    actions.appendChild(doneButton);

    panel.appendChild(title);
    panel.appendChild(field);
    panel.appendChild(status);
    panel.appendChild(note);
    panel.appendChild(actions);
    rootNode.appendChild(backdrop);
    rootNode.appendChild(panel);
    doc.body.appendChild(rootNode);

    const modal = {
      doc,
      root: rootNode,
      field,
      status,
      copyButton,
      doneButton,
      opener: null,
      onKeydown: null,
    };

    backdrop.addEventListener('click', () => closeModal(modal));
    doneButton.addEventListener('click', () => closeModal(modal));
    copyButton.addEventListener('click', () => {
      copyText(modal.field.value)
        .then(() => { modal.status.textContent = COPY_SUCCESS_STATUS; })
        .catch(() => { modal.status.textContent = COPY_MANUAL_STATUS; });
    });

    doc.body.hlaCartShareModal = modal;
    return modal;
  }

  // aria-modal="true" tells assistive tech the rest of the page is unavailable. That is a
  // lie unless Tab actually stays inside the panel, so the trap below is not optional.
  function focusables(modal) {
    return [modal.field, modal.copyButton, modal.doneButton].filter(node => node && !node.disabled);
  }

  function trapTab(modal, event) {
    const order = focusables(modal);
    if (order.length === 0) return;
    const active = modal.doc.activeElement;
    const index = order.indexOf(active);
    const back = Boolean(event.shiftKey);
    let next;
    if (index === -1) next = back ? order[order.length - 1] : order[0];
    else next = order[(index + (back ? -1 : 1) + order.length) % order.length];
    if (typeof event.preventDefault === 'function') event.preventDefault();
    if (next && typeof next.focus === 'function') next.focus();
  }

  function openModal(modal, link, statusText, opener) {
    if (!modal) return null;
    modal.opener = opener || null;
    modal.field.value = link;
    // Unhide BEFORE writing the status: a live region that is populated while its
    // ancestor is still hidden is usually not announced when it later appears.
    modal.status.textContent = '';
    modal.root.hidden = false;
    modal.status.textContent = statusText || '';
    if (!modal.onKeydown) {
      modal.onKeydown = event => {
        if (!event) return;
        if (event.key === 'Escape' || event.key === 'Esc') closeModal(modal);
        else if (event.key === 'Tab') trapTab(modal, event);
      };
      modal.doc.addEventListener('keydown', modal.onKeydown);
    }
    if (typeof modal.field.focus === 'function') modal.field.focus();
    if (typeof modal.field.select === 'function') modal.field.select();
    return modal;
  }

  function closeModal(modal) {
    if (!modal || modal.root.hidden) return modal;
    modal.root.hidden = true;
    if (modal.onKeydown) {
      modal.doc.removeEventListener('keydown', modal.onKeydown);
      modal.onKeydown = null;
    }
    const opener = modal.opener;
    modal.opener = null;
    // Dawn re-renders the drawer on cart updates, which can detach the button that
    // opened this. Focusing a detached node silently drops focus to <body>, so fall
    // back to whichever live trigger is on the page now.
    const target = opener && opener.isConnected !== false
      ? opener
      : modal.doc.querySelector('[data-hla-cart-share-copy]');
    if (target && typeof target.focus === 'function') target.focus();
    return modal;
  }

  async function handleCopy(widget) {
    let payload;
    try {
      const cart = await loadWidgetCart(widget);
      payload = buildSharePayload(cart, root.location && root.location.origin);
    } catch (_) {
      setWidgetState(widget, 'error', ERROR_HELPER, 'Could not copy the link.');
      return;
    }
    // Empty cart / no permalink keeps the inline empty-state; there is no link to show.
    if (payload.empty || !payload.permalink) return;

    let status = COPY_SUCCESS_STATUS;
    try {
      await copyText(payload.permalink);
    } catch (_) {
      status = COPY_MANUAL_STATUS;
    }
    // The popup opens either way — a blocked clipboard still leaves a copyable link on screen.
    const modal = ensureModal(root.document);
    openModal(modal, payload.permalink, status, widgetElements(widget).copy);
  }

  async function handleEmail(widget) {
    try {
      const cart = await loadWidgetCart(widget);
      const payload = buildSharePayload(cart, root.location && root.location.origin);
      if (payload.empty || !payload.email.url) return;
      if (payload.email.fallback && root.console && typeof root.console.log === 'function') {
        root.console.log('[HLA Cart Share] Cart permalink omitted from email because the mailto URL exceeded the safe length.');
      }
      root.location.href = payload.email.url;
    } catch (_) {
      setWidgetState(widget, 'error', ERROR_HELPER, 'Could not open the email draft.');
    }
  }

  function initWidget(widget) {
    if (!widget || widget.dataset.hlaCartShareInit === 'true') return widget;
    widget.dataset.hlaCartShareInit = 'true';
    const elements = widgetElements(widget);
    if (elements.copy) elements.copy.addEventListener('click', () => handleCopy(widget));
    if (elements.email) elements.email.addEventListener('click', () => handleEmail(widget));
    loadWidgetCart(widget).catch(() => {});
    return widget;
  }

  function initAll(doc = root.document) {
    if (!doc) return [];
    const widgets = Array.from(doc.querySelectorAll('[data-hla-cart-share]')).map(initWidget);
    if (root.MutationObserver && doc.body && !doc.body.hlaCartShareObserver) {
      const observer = new root.MutationObserver(() => {
        doc.querySelectorAll('[data-hla-cart-share]').forEach(initWidget);
      });
      observer.observe(doc.body, { childList: true, subtree: true });
      doc.body.hlaCartShareObserver = observer;
    }
    return widgets;
  }

  return {
    COPY_MANUAL_STATUS,
    COPY_SUCCESS_STATUS,
    DEFAULT_MAX_MAILTO_URL_LENGTH,
    MODAL_NOTE,
    buildCartPermalink,
    buildEmailCartUrl,
    buildLineItemList,
    buildSharePayload,
    cartItems,
    closeModal,
    ensureModal,
    handleCopy,
    initAll,
    initWidget,
    moneyFromCents,
    openModal,
  };
});
